import { Fingerprint } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';

import { describeBiometricError, hasPlatformAuthenticator, loginWithBiometrics } from '../lib/biometrics';
import { useSession } from '../lib/session';

export function LoginPage(): React.JSX.Element {
  const status = useSession((s) => s.status);
  const signIn = useSession((s) => s.signIn);
  const applySession = useSession((s) => s.applySession);
  const error = useSession((s) => s.error);
  const clearError = useSession((s) => s.clearError);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [canUseBiometrics, setCanUseBiometrics] = useState(false);
  const [biometricError, setBiometricError] = useState<string | null>(null);

  // Solo se ofrece el botón si el dispositivo puede de verdad. Un computador sin lector
  // de huellas soporta WebAuthn y no puede usarlo: poner el botón igual sería ofrecer
  // algo que al pulsarlo no lleva a ninguna parte.
  useEffect(() => {
    void hasPlatformAuthenticator().then(setCanUseBiometrics);
  }, []);

  if (status === 'authenticated') return <Navigate to="/" replace />;

  const handleBiometrics = async (): Promise<void> => {
    setBiometricError(null);
    setSending(true);
    try {
      const result = await loginWithBiometrics(email.trim() || undefined);
      applySession(result);
      navigate('/', { replace: true });
    } catch (caught) {
      setBiometricError(describeBiometricError(caught));
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSending(true);
    try {
      await signIn(email, password);
      navigate('/', { replace: true });
    } catch {
      // El error ya queda en el estado de la sesión y se pinta abajo.
    } finally {
      setSending(false);
    }
  };

  return (
    <AuthLayout title="Entra a tu cuenta" subtitle="Control total de tus gastos">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label className="ed-label" htmlFor="email">
            Correo electrónico
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
            className="ed-input"
            placeholder="tucorreo@ejemplo.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              if (error) clearError();
            }}
          />
        </div>

        <div>
          <label className="ed-label" htmlFor="password">
            Contraseña
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            className="ed-input"
            placeholder="Tu contraseña"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) clearError();
            }}
          />
        </div>

        {error && (
          <p role="alert" className="rounded-ed border border-ed-coral-deep/50 bg-ed-coral-deep/15 px-3 py-2 text-sm text-ed-coral-light">
            {error}
          </p>
        )}

        {biometricError && (
          <p role="alert" className="rounded-ed border border-ed-coral-deep/50 bg-ed-coral-deep/15 px-3 py-2 text-sm text-ed-coral-light">
            {biometricError}
          </p>
        )}

        <button type="submit" className="ed-btn-primary w-full" disabled={sending}>
          {sending ? 'Entrando…' : 'Entrar'}
        </button>
      </form>

      {/* La huella, si el dispositivo puede. Va **después** de la contraseña y con menos
          peso visual: es la alternativa cómoda, no la principal. Si alguien se queda sin
          el dispositivo, lo primero que busca es dónde escribir su contraseña. */}
      {canUseBiometrics && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-3">
            <span className="h-px flex-1 bg-ed-bg-border/40" aria-hidden="true" />
            <span className="text-xs text-ed-text-muted">o</span>
            <span className="h-px flex-1 bg-ed-bg-border/40" aria-hidden="true" />
          </div>

          <button
            type="button"
            className="ed-btn-secondary w-full"
            disabled={sending}
            onClick={() => void handleBiometrics()}
          >
            <Fingerprint size={18} aria-hidden="true" />
            Entrar con la huella
          </button>

          <p className="text-center text-[11px] text-ed-text-muted">
            Funciona si vinculaste este dispositivo desde los ajustes.
          </p>
        </div>
      )}

      <p className="mt-6 text-center text-sm text-ed-text-muted">
        ¿Todavía no tienes cuenta?{' '}
        <Link to="/crear-cuenta" className="font-semibold text-ed-gold hover:underline">
          Crear una
        </Link>
      </p>
    </AuthLayout>
  );
}

/** Marco común de las pantallas de entrada, con el logotipo de la marca. */
export function AuthLayout({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="ed-safe-top ed-safe-bottom flex min-h-[100dvh] flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <img
            src="/android-chrome-512x512.png"
            alt="ElDescuadre"
            width={96}
            height={96}
            className="mx-auto mb-4 h-24 w-24 rounded-3xl shadow-ed-glow-gold"
          />
          <h1 className="font-display text-2xl font-extrabold tracking-tight">
            El<span className="text-ed-gold">Descuadre</span>
          </h1>
          <p className="mt-1 text-xs uppercase tracking-[0.2em] text-ed-text-silver">{subtitle}</p>
        </div>

        <div className="ed-card p-5">
          <h2 className="mb-4 font-display text-lg font-bold">{title}</h2>
          {children}
        </div>
      </div>
    </div>
  );
}
